import { useMemo } from 'react';
import type { FeatureCollection } from 'geojson';
import { Layer, Source } from 'react-map-gl/mapbox';
import {
  STATISTIC_BUBBLE_BORDER_WIDTH_PX,
  STATISTIC_CIRCLE_LAYER_ID,
  STATISTIC_CIRCLE_RADIUS_BY_TOTAL_STOPS,
  STATISTIC_FONT_PX_GTE_CHARS,
} from '~/constants/map';
import {
  STATISTIC_BUBBLE_OPACITY,
  STATISTIC_BUBBLE_TEXT_COLOR,
  STATISTIC_BUBBLE_TEXT_HALO_BLUR,
  STATISTIC_BUBBLE_TEXT_HALO_COLOR,
  STATISTIC_BUBBLE_TEXT_HALO_WIDTH,
  STATISTIC_BUBBLE_BORDER_COLOR,
  STATISTIC_CIRCLE_COLOR_TOTAL_STOPS_FOR_INTERPOLATE,
} from '~/constants/statisticLayerTheme';
import type { IMapStatisticDatum } from '~/utils/interface/map';

interface StatisticLayerProps {
  statisticData: IMapStatisticDatum[];
}

function linearInterpolate(input: unknown[], pairs: readonly (readonly [number, unknown])[]): unknown[] {
  const flat: unknown[] = [];
  pairs.forEach(([stop, val]) => {
    flat.push(stop, val);
  });
  return ['interpolate', ['linear'], input, ...flat];
}

function statisticFeatureCollection(rows: IMapStatisticDatum[]): FeatureCollection {
  const features = rows
    .filter(
      (r) => r != null && typeof r === 'object' && Number.isFinite(Number(r.lat)) && Number.isFinite(Number(r.lng))
    )
    .map((row) => {
      const lat = Number(row.lat);
      const lng = Number(row.lng);
      const total = typeof row.total === 'number' && Number.isFinite(row.total) ? Math.max(0, row.total) : 0;
      return {
        type: 'Feature' as const,
        properties: {
          total,
          name: row.name ?? '',
          code: row.code ?? '',
        },
        geometry: { type: 'Point' as const, coordinates: [lng, lat] },
      };
    });
  return { type: 'FeatureCollection', features };
}

function StatisticLayer({ statisticData }: StatisticLayerProps) {
  const geo = useMemo(() => statisticFeatureCollection(statisticData?.length ? statisticData : []), [statisticData]);

  const totalForExpr: unknown[] = ['coalesce', ['to-number', ['get', 'total']], 0];
  const circleRadiusExpr = linearInterpolate(totalForExpr, [...STATISTIC_CIRCLE_RADIUS_BY_TOTAL_STOPS]);
  const circleColorExpr = linearInterpolate(totalForExpr, [...STATISTIC_CIRCLE_COLOR_TOTAL_STOPS_FOR_INTERPOLATE]);

  const digitLenExpr: unknown[] = ['length', ['to-string', ['get', 'total']]];
  const textSizeExpr = [
    'case',
    ['>=', digitLenExpr, 8],
    STATISTIC_FONT_PX_GTE_CHARS.GTE_DIGITS_8,
    ['>=', digitLenExpr, 6],
    STATISTIC_FONT_PX_GTE_CHARS.GTE_DIGITS_6,
    ['>=', digitLenExpr, 4],
    STATISTIC_FONT_PX_GTE_CHARS.GTE_DIGITS_4,
    STATISTIC_FONT_PX_GTE_CHARS.FALLBACK,
  ];

  const textFieldExpr = ['to-string', ['get', 'total']] as unknown as never;

  if (!geo.features.length) {
    return null;
  }

  return (
    <Source id='vn-statistic-source' type='geojson' data={geo}>
      <Layer
        id={STATISTIC_CIRCLE_LAYER_ID}
        type='circle'
        paint={
          {
            'circle-radius': circleRadiusExpr as never,
            'circle-color': circleColorExpr as never,
            'circle-stroke-width': STATISTIC_BUBBLE_BORDER_WIDTH_PX,
            'circle-stroke-color': STATISTIC_BUBBLE_BORDER_COLOR,
            'circle-opacity': STATISTIC_BUBBLE_OPACITY,
          } as never
        }
      />
      <Layer
        id='vn-statistic-total-symbol'
        type='symbol'
        layout={
          {
            'text-field': textFieldExpr,
            'text-size': textSizeExpr as unknown as never,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Regular'],
            'text-anchor': 'center',
            'text-allow-overlap': true,
            'text-ignore-placement': true,
            'text-padding': 2,
          } as never
        }
        paint={
          {
            'text-color': STATISTIC_BUBBLE_TEXT_COLOR,
            'text-halo-color': STATISTIC_BUBBLE_TEXT_HALO_COLOR,
            'text-halo-width': STATISTIC_BUBBLE_TEXT_HALO_WIDTH,
            'text-halo-blur': STATISTIC_BUBBLE_TEXT_HALO_BLUR,
          } as never
        }
      />
    </Source>
  );
}

export default StatisticLayer;
